/**
 * What: this is a component that tracks products
 * Why: simplify product tracking
 * How: over all we use same data for impressions and interactions so why not use one component for it
 *      1. wrap the element we want to track with product component
 *          - product component should additionally wrap the element that captures interactions and handles them
 *      2. if the element is visible trigger tracking event
 *
 * End goal -> wrap the element that need to be tracked for product, provide props and thats that, rest is handled in tracking package
 *
 */

import {Price} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//price.types';
import {
  BrandTracker,
  ProductTracker,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//tracker.types';
import {trackProductInteraction} from '@aboutyou/legacy-tracking-sdk';
import React, {FC} from 'react';
import {trackingProductGetter} from '../utils/trackingProductGetter';
import {useTrackingFactory} from '../utils/useTrackingFactory';
import {useTracking} from '../utils/utils';
import {BaseTracker, BaseTrackerProps} from './BaseTracker';
import {ProductTrackingData} from './TrackPromotions';

export interface ProductTileTracker {
  product: ProductTracker;
  brand: BrandTracker;
  price?: Price;
}

interface TrackProductProps extends Omit<BaseTrackerProps, 'track'> {
  productTile: ProductTileTracker;
  streamPosition: number;
  trackingData?: ProductTrackingData;
  onImpressionTracked?: VoidFunction;
  disabled?: boolean;
  badges: string[];
}

export const TrackProduct: FC<TrackProductProps> = ({
  children,
  productTile,
  streamPosition,
  trackingData,
  disabled,
  badges,
  ...rest
}) => {
  const trackProduct = useProductTracking({
    productTile,
    streamPosition,
    trackingData,
    disabled,
    badges,
  });

  const handleTrack = (type: 'impression' | 'click') => {
    trackProduct(type);
  };

  return (
    <BaseTracker track={handleTrack} {...rest} disabled={disabled}>
      {children}
    </BaseTracker>
  );
};

function useProductTracking({
  productTile,
  streamPosition,
  trackingData,
  disabled,
  badges,
}: {
  productTile: ProductTileTracker;
  streamPosition: number;
  trackingData?: TrackProductProps['trackingData'];
  disabled?: boolean;
  badges: string[];
}) {
  const {pageContextCallback} = useTracking();

  return useTrackingFactory(
    () => ({
      ...trackingProductGetter({
        product: productTile.product,
        price: productTile.price,
        brand: productTile.brand,
        streamPosition,
        sortChannel: trackingData?.sortingChannel,
        pageType: trackingData?.pageType ?? pageContextCallback().type,
        recoType: trackingData?.recoType,
        headline: trackingData?.headline,
        elements: trackingData?.elements,
        sectionIndex: trackingData?.sectionIndex,
        rowIndexInSection: trackingData?.rowIndexInSection,
        creativeName: trackingData?.creativeName,
        promotionId: trackingData?.promotionId,
        blockTypeAndId: trackingData?.blockTypeAndId,
        badges,
      }),
      disabled,
    }),
    trackProductInteraction,
  );
}
